import APP_CONFIG from '@/config/app';
import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import TrayBody from './TrayBody';
import TrayFooter from './TrayFooter';
import TrayHeader from './TrayHeader';
import { TrayBottomDrawerProps } from './interfaces/Tray';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border border-white'
  : '';
const { MAIN_BACKDROP_BLUR } = APP_CONFIG.STYLES;
const mainPadding = APP_CONFIG.STYLES.MAIN_PADDING;
const mainGap = APP_CONFIG.STYLES.MAIN_GAP;
const mainRoundedness = APP_CONFIG.STYLES.MAIN_ROUNDEDNESS;
const spacingConfig = {
  padding: `p${mainPadding}`,
  gap: `gap${mainGap}`,
};
const roundednessConfig = {
  roundedness: `rounded${mainRoundedness}`,
};
const sizingConfig = {
  height: 'h-3/5',
};
const colorConfig = {
  background: `bg-primary/10 backdrop-blur${MAIN_BACKDROP_BLUR}`,
  border: 'border border-primary/10',
};
const TrayBottomDrawer: FC<TrayBottomDrawerProps> = ({
  isOpen,
  showHeaderCloseButton,
  onClose,
  headerTitle,
  headerContent: headerNode,
  headerRightButtons,
  bodyHeadlineTitle,
  bodyHeadlineCaption,
  bodyChildren,
  footerContent,
  includeFooterCloseButton,
  footerCloseButtonLabel,
  parentRef,
  extraButton1Label,
  extraButton1OnClick,
  extraButton2Label,
  extraButton2OnClick,
  extraButton1Icon,
  extraButton2Icon,
  extraButton1Props,
  extraButton2Props,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [drawerStyles, setDrawerStyles] = useState<React.CSSProperties>({});
  const [parentBottom, setParentBottom] = useState<number>(0);
  const [localIsOpen, setLocalIsOpen] = useState(isOpen);
  const resolveCssVariable = (variableName: string): number => {
    const rootStyles = getComputedStyle(document.documentElement);
    const value = rootStyles.getPropertyValue(variableName);
    return parseFloat(value) * parseFloat(rootStyles.fontSize);
  };
  const bottomMargin = resolveCssVariable('--md');
  const sideMargin = resolveCssVariable('--md');
  const handleOnClose = (event?: MouseEvent | React.MouseEvent) => {
    if (event) event.stopPropagation();
    setLocalIsOpen(false);
    onClose?.();
  };
  const updateDrawerStyles = useCallback(() => {
    if (parentRef?.current) {
      const parentRect = parentRef.current.getBoundingClientRect();
      const drawerHeight = 0.6 * parentRect.height;
      const viewportHeight = window.innerHeight;
      const drawerTop = viewportHeight - drawerHeight - 2 * bottomMargin;
      setDrawerStyles({
        position: 'fixed',
        left: parentRect.left + sideMargin,
        top: drawerTop,
        height: drawerHeight,
        width: parentRect.width - 2 * sideMargin,
        zIndex: 40,
      });
    }
  }, [parentRef, sideMargin, bottomMargin]);
  useEffect(() => {
    setLocalIsOpen(isOpen);
    if (isOpen) updateDrawerStyles();
  }, [isOpen, parentRef, updateDrawerStyles]);
  useEffect(() => {
    const handleResize = () => {
      updateDrawerStyles();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [parentRef, updateDrawerStyles]);
  const slideAnimationConfig = APP_CONFIG.ANIMATION_SETTINGS.SLIDE_FROM.BOTTOM;
  const adjustedVariants = {
    initial: {
      opacity: 0,
      y: window.innerHeight - parentBottom,
    },
    open: slideAnimationConfig.open,
    closed: slideAnimationConfig.closed,
  };
  const drawerContent = (
    <AnimatePresence>
      {localIsOpen && (
        <motion.div
          ref={drawerRef}
          initial="initial"
          animate="open"
          exit="closed"
          variants={adjustedVariants}
          transition={APP_CONFIG.ANIMATION_SETTINGS.COMMON}
          style={drawerStyles}
          className={`flex flex-col 
            ${spacingConfig.padding}
            ${spacingConfig.gap} 
            ${sizingConfig.height} 
            ${colorConfig.background} 
            ${colorConfig.border} 
            ${roundednessConfig.roundedness} 
            ${borderClassToVisualizeElementsAndSpacing}`}
          onAnimationComplete={() => {
            if (!localIsOpen && onClose) {
              onClose();
            }
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <TrayHeader
              title={typeof headerTitle === 'string' ? headerTitle : undefined}
              headerNode={headerNode}
              onClose={handleOnClose}
              rightButtons={headerRightButtons}
              showCloseButton={showHeaderCloseButton}
            />
          </div>
          <TrayBody
            headlineTitle={bodyHeadlineTitle}
            headlineCaption={bodyHeadlineCaption}
          >
            {bodyChildren}
          </TrayBody>
          <div onClick={(e) => e.stopPropagation()}>
            <TrayFooter
              footerContent={footerContent}
              includeCloseButton={includeFooterCloseButton}
              closeButtonLabel={footerCloseButtonLabel}
              onClose={handleOnClose}
              extraButton1Label={extraButton1Label}
              extraButton1OnClick={extraButton1OnClick}
              extraButton2Label={extraButton2Label}
              extraButton2OnClick={extraButton2OnClick}
              extraButton1Icon={extraButton1Icon}
              extraButton2Icon={extraButton2Icon}
              extraButton1Props={extraButton1Props}
              extraButton2Props={extraButton2Props}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
  return createPortal(drawerContent, document.body);
};
export default TrayBottomDrawer;
